import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouterProps } from '../../utils/with-router-props'
import { Link } from 'react-router-dom'
import SHSForm from '../../utils/shs-form'
import InputElement from '../../utils/input'
import { callApi, fetchApiDataIfNeeded } from '../../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'
import { createSelector } from '@reduxjs/toolkit'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SearchComplete from '../../utils/search-complete'

const selectTemplate = (state, templateId) => state.adminInputTemplates?.find(t => t.id === Number(templateId))
const selectInputGroups = (state, templateId) => state.adminTemplateGroups?.filter(g => g.templateId === Number(templateId)) || []

const makeAdminInputGroups = createSelector(
    [selectInputGroups],
    (inputGroups) => {
        return inputGroups.sort((a, b) => a.displayOrder - b.displayOrder)
    }
)

const mapStateToProps = (state, ownProps) => {
    const adminInputTemplate = selectTemplate(state, ownProps.params.templateId)
    const adminInputGroups = makeAdminInputGroups(state, ownProps.params.templateId)
    return { adminInputTemplate, adminInputGroups }
}

const AdminTemplateView = ({ dispatch, navigate, params, adminInputTemplate, adminInputGroups }) => {
    const [showAddGroup, setShowAddGroup] = useState(false)
    const [inputOpen, setInputOpen] = useState(false)
    const [draggedGroup, setDraggedGroup] = useState(null)

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/input-templates', 'VIEW', params.templateId)))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/template-groups', 'LIST'), { filter: [`template_id|${params.templateId}`] }))
    }, [dispatch, params.templateId])

    const renderGroupRow = (group, index) => {
        const handleDragStart = (e) => {
            setDraggedGroup(group)
            e.dataTransfer.effectAllowed = 'move'
            e.currentTarget.style.opacity = '0.4'
        }

        const handleDragEnd = (e) => {
            e.currentTarget.style.opacity = '1'
            setDraggedGroup(null)
        }

        const handleDragOver = (e) => {
            if (e.preventDefault) {
                e.preventDefault()
            }
            e.dataTransfer.dropEffect = 'move'
            return false
        }

        const handleDragEnter = (e) => {
            e.currentTarget.classList.add('drag-over')
        }

        const handleDragLeave = (e) => {
            e.currentTarget.classList.remove('drag-over')
        }

        const handleDrop = (e) => {
            if (e.stopPropagation) {
                e.stopPropagation()
            }
            e.currentTarget.classList.remove('drag-over')

            if (draggedGroup && draggedGroup.id !== group.id) {
                handleReorderGroup(draggedGroup, group)
            }

            return false
        }
        return (
            <tr key={group.id}
                draggable="true"
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <td className="index">
                    <FontAwesomeIcon icon="grip-vertical" />
                    {index}
                </td>
                <td className="main">{group?.groupTitle}</td>
                <td className="main"><Link to={buildRoutePath(routes.adminTemplateGroupEdit.path, { templateId: params.templateId, groupId: group.groupId })}>{group?.groupTitle}</Link></td>
                {/* <td className="main">
                    <Link to={buildRoutePath(routes.adminTemplateGroupView.path, { groupId: group.groupId })}>
                        Manage Questions
                    </Link>
                </td> */}
                <td className="main">
                    <Link className="btn outline small" onClick={() => handleRemoveGroup(group)}>Remove</Link>
                </td>
            </tr>
        )
    }

    const renderGroups = () => {
        if (!adminInputGroups.length) {
            return <p>No groups added to this template yet</p>
        }
        let allGroups = []
        adminInputGroups?.forEach((group, idx) => {
            allGroups.push(renderGroupRow(group, idx + 1))
        })
        return (
            <table className="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    {allGroups}
                </tbody>
            </table>
        )
    }

    const submitFX = (formData, formId) => {
        formData.isActive = true
        formData.templateId = adminInputTemplate.id
        dispatch(callApi(apiResourceEndpoint('admin/input-groups', 'CREATE'), formData, formId))
    }

    const successFX = () => {
        navigate(buildRoutePath(routes.adminTemplateGroupIndex.path))
        setShowAddGroup(false)
    }

    const handleDelete = () => {
        const confirm = window.confirm('Are you sure you want to delete this template?')
        if (confirm) {
            dispatch(callApi(apiResourceEndpoint('admin/input-templates', 'DELETE', params.templateId), {}, -1))
                .then(() => {
                    navigate(buildRoutePath(routes.adminTemplateIndex.path))
                })
        }
    }

    const submitChangeStatus = (formData, formId) => {
        dispatch(callApi(apiResourceEndpoint('admin/input-templates', 'UPDATE', params.templateId), formData, formId))
    }

    const handleAddGroup = async (group) => {
        dispatch(callApi(apiResourceEndpoint('admin/input-templates', 'UPDATE', params.templateId, 'add-group'), { groupId: group.id }, -1))
            .then(() => {
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/template-groups', 'LIST'), { filter: [`template_id|${params.templateId}`] }))
            })
    }

    const handleReorderGroup = (draggedGroupItem, targetGroup) => {
        const orderMap = {}
        adminInputGroups.forEach(ig => {
            orderMap[ig.groupId] = ig.displayOrder
        })

        let oldPosition = Number(orderMap[draggedGroupItem.groupId])
        let newPosition = Number(orderMap[targetGroup.groupId])

        // Fallback to rendered order if displayOrder values are duplicated or missing.
        if (!oldPosition || !newPosition || oldPosition === newPosition) {
            oldPosition = adminInputGroups.findIndex(group => Number(group.groupId) === Number(draggedGroupItem.groupId)) + 1
            newPosition = adminInputGroups.findIndex(group => Number(group.groupId) === Number(targetGroup.groupId)) + 1
        }

        if (!oldPosition || !newPosition || oldPosition === newPosition) {
            return
        }

        dispatch(callApi(apiResourceEndpoint('admin/input-templates', 'UPDATE', params.templateId, 'update-group-order'), {
            groupId: draggedGroupItem.groupId,
            oldPosition,
            newPosition
        }, -1))
    }

    const handleRemoveGroup = (group) => {
        window.confirm('Are you sure you want to remove this group from the template? This will not delete the group or any associated questions, it will just unassign it from the template.') &&
        dispatch(callApi(apiResourceEndpoint('admin/input-templates', 'UPDATE', params.templateId, 'remove-group'), { groupId: group.groupId }, -1))
            .then(() => {
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/template-groups', 'LIST'), { filter: [`template_id|${params.templateId}`] }))
            })
    }

    const renderGroupSearch = () => {
        const groupsIds = adminInputGroups.map(group => group.groupId)
        let excludeFilter = []
        if (groupsIds.length > 0) {
            excludeFilter = [`id|${groupsIds.join(',')}|WHERE_NOT_IN`]
        }

        return (
            <div className="search-complete">
                <SearchComplete
                    key={adminInputGroups.length}
                    label="Search Groups"
                    searchEndpoint="admin/input-groups"
                    renderTemplate={(group) => group.name}
                    excludeFilter={excludeFilter}
                    onHandleSelect={handleAddGroup}
                    allowCreation={false}
                    placeholder="Search and Add Existing Groups to the Template" />
            </div>
        )
    }

    if (!adminInputTemplate || adminInputTemplate.length === 0) {
        return null
    }

    const renderAddGroup = () => {
        if (!showAddGroup) {
            return null
        }
        return (
            <div className="container">
                <div className="content-box">
                    <h3>Create a New Template Group for {adminInputTemplate.name}</h3>
                    <SHSForm extraClass="flex-form" submitFX={submitFX} successFX={successFX} showCancel={true} cancelAction={() => setShowAddGroup(false)}>
                        <InputElement
                            type="text"
                            id="name"
                            label="Section Name"
                            placeholder="Basic Truck Information"
                            extraClass="flex-50"
                            required />
                        <InputElement
                            type="integer"
                            id="displayOrder"
                            label="Display Order"
                            placeholder="1"
                            extraClass="flex-50"
                            required />
                    </SHSForm>
                </div>
            </div>
        )
    }

    return (
        <div className="admin-dashboard">
            <div className="header">
                <div className="container">
                    <div className="header-details">
                        { !inputOpen &&
                            <h1 className="clickable" onClick={() => setInputOpen(!inputOpen)}>{adminInputTemplate.name}</h1>
                        }
                        { inputOpen &&
                                <SHSForm showCancel={true} cancelAction={() => setInputOpen(!inputOpen)} formClass="inline-form" submitFX={submitChangeStatus} successFX={() => setInputOpen(!inputOpen)}>
                                    <InputElement
                                        type="text"
                                        id="name"
                                        default={adminInputTemplate.name} />
                                </SHSForm>
                        }
                        <p><small>Click title above to edit</small></p>
                    </div>
                </div>
            </div>
            <div className="content-wrapper">
                <div className="dashboard-wrapper admin-box">
                    <div className="container">
                        {/* <h3>Current Template Groups</h3> */}
                        {renderGroups()}
                        {!showAddGroup &&
                            <div className="content-box">
                                <h3>Add Additional Groups</h3>
                                {renderGroupSearch()}
                            </div>
                        }
                    </div>
                    {renderAddGroup()}
                    <div className="container">
                        <div className="content-box delete-wrapper">
                            <div className="btn small red" onClick={handleDelete}>
                                DELETE TEMPLATE <FontAwesomeIcon icon="trash-alt" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    )
}

export default withRouterProps(connect(mapStateToProps)(AdminTemplateView))
